import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Stack, Table } from 'react-bootstrap';
import { Download, Search } from 'react-bootstrap-icons';
import { useTicketsInJudgmentQuery } from '../../queries/ticketsInJudgment.query';
import { useExportTicketsInJudgmentMutation } from '../../mutations/ticketsInJudgment.mutation';
import { TicketsInJudgmentResponse } from '../../types/ticketsInJudgment.types';
import MonitoringListSelect from '../FormComponents/MonitoringListSelect';

function TicketsInJudgmentReport() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [monitoringListId, setMonitoringListId] = useState('');
  const [excludeDelinquent, setExcludeDelinquent] = useState(false);
  const [reportData, setReportData] = useState<TicketsInJudgmentResponse | null>(null);

  const { mutate: fetchTickets, isLoading: isFetching } = useTicketsInJudgmentQuery();
  const { mutate: exportTickets, isLoading: isExporting } = useExportTicketsInJudgmentMutation();

  useEffect(() => {
    // Load all tickets on mount (no date filter required)
    fetchTickets(
      {},
      {
        onSuccess: (data) => {
          setReportData(data);
        }
      }
    );
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    fetchTickets(
      {
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        monitoringListId: monitoringListId || undefined,
        excludeDelinquent
      },
      {
        onSuccess: (data) => {
          setReportData(data);
        }
      }
    );
  };

  const handleExport = () => {
    exportTickets({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      monitoringListId: monitoringListId || undefined,
      excludeDelinquent
    });
  };

  const handleMonitoringListChange = (_key: string, value: string) => {
    setMonitoringListId(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
  };

  return (
    <Card className="mt-4">
      <Card.Header>
        <h5 className="mb-0">Tickets In Judgment Report</h5>
      </Card.Header>
      <Card.Body>
        <Form onSubmit={handleSearch}>
          <Row className="align-items-end mb-3">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Start Date (Optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>End Date (Optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Monitoring List</Form.Label>
                <MonitoringListSelect
                  handleDataChange={handleMonitoringListChange}
                  value={monitoringListId}
                  addNew={false}
                  showAllOption={true}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button type="submit" disabled={isFetching}>
                {isFetching ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <Search className="me-1" /> Search
                  </>
                )}
              </Button>
            </Col>
            {reportData && reportData.tickets.length > 0 && (
              <Col md={3} className="text-end d-flex align-items-end justify-content-end">
                <Button variant="outline-primary" onClick={handleExport} disabled={isExporting}>
                  {isExporting ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Download className="me-1" /> Export CSV
                    </>
                  )}
                </Button>
              </Col>
            )}
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Check
                type="checkbox"
                id="excludeDelinquent"
                label="Exclude delinquent tickets"
                checked={excludeDelinquent}
                onChange={(e) => setExcludeDelinquent(e.target.checked)}
              />
            </Col>
          </Row>
        </Form>

        {reportData && (
          <>
            <Stack direction="horizontal" gap={3} className="mb-3 flex-wrap">
              <Badge bg="secondary" pill>
                Total Tickets: {reportData.totalCount}
              </Badge>
              <Badge bg="danger" pill>
                Total Amount Due: {formatCurrency(reportData.totalAmountDue)}
              </Badge>
              <Badge bg="warning" text="dark" pill>
                Total Interest: {formatCurrency(reportData.totalInterestOwed)}
              </Badge>
            </Stack>

            {reportData.tickets.length > 0 ? (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Summons Number</th>
                    <th>Judgment Date</th>
                    <th>Plate Number</th>
                    <th>Client Name</th>
                    <th>Client Email</th>
                    <th>Fine</th>
                    <th>Penalty</th>
                    <th>Interest</th>
                    <th>Amount Due</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.tickets.map((ticket) => (
                    <tr key={ticket.ticketId}>
                      <td>{ticket.summonsNumber}</td>
                      <td>{formatDate(ticket.judgmentEntryDate)}</td>
                      <td>{ticket.plateNumber}</td>
                      <td>{ticket.clientName}</td>
                      <td>{ticket.clientEmail}</td>
                      <td>{formatCurrency(ticket.fineAmount)}</td>
                      <td>{formatCurrency(ticket.penaltyAmount)}</td>
                      <td>{formatCurrency(ticket.interestAmount)}</td>
                      <td>{formatCurrency(ticket.amountDue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p className="text-muted text-center">
                No tickets in judgment found for the selected date range.
              </p>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

export default TicketsInJudgmentReport;
